/**
 * Purchase order API service (S3.1 contract, S3.2 surface consumers) — thin
 * typed client over the MSW handlers in `mocks/handlers/purchaseOrder.ts`.
 *
 * S3.2 additions: full list-param passthrough (status / approvalStatus /
 * vendorId filters), `send` (draft → sent, blocked by the >NT$200k approval
 * gate), the both-documents `listAllocations` read (first-class SO↔PO join),
 * and the on-demand 檢查第二次 `secondCheck` aids.
 */

import apiClient from './axios.config';
import { API_ROUTES } from '@/constants/api.constants';
import type {
  AllocationListParams,
  AllocationListResponse,
  CreatePurchaseOrderRequest,
  PoLineSecondCheck,
  PurchaseOrder,
  PurchaseOrderDecisionRequest,
  PurchaseOrderListParams,
  PurchaseOrderListResponse,
} from '@/types/api';

export const purchaseOrderApi = {
  list: async (params?: PurchaseOrderListParams): Promise<PurchaseOrderListResponse> => {
    const { data } = await apiClient.get<PurchaseOrderListResponse>(API_ROUTES.PURCHASE_ORDERS, {
      params,
    });
    return data;
  },

  getById: async (id: string): Promise<{ data: PurchaseOrder }> => {
    const { data } = await apiClient.get<{ data: PurchaseOrder }>(
      `${API_ROUTES.PURCHASE_ORDERS}/${id}`
    );
    return data;
  },

  create: async (body: CreatePurchaseOrderRequest): Promise<{ data: PurchaseOrder }> => {
    const { data } = await apiClient.post<{ data: PurchaseOrder }>(
      API_ROUTES.PURCHASE_ORDERS,
      body
    );
    return data;
  },

  /** >NT$200k manager release (ReasonRequiredModal — actor + reason mandatory). */
  decide: async (id: string, body: PurchaseOrderDecisionRequest): Promise<{ data: PurchaseOrder }> => {
    const { data } = await apiClient.post<{ data: PurchaseOrder }>(
      `${API_ROUTES.PURCHASE_ORDERS}/${id}/decision`,
      body
    );
    return data;
  },

  /** draft → sent (409 APPROVAL_REQUIRED while the 200k gate is pending). */
  send: async (id: string): Promise<{ data: PurchaseOrder }> => {
    const { data } = await apiClient.post<{ data: PurchaseOrder }>(
      `${API_ROUTES.PURCHASE_ORDERS}/${id}/send`,
      {}
    );
    return data;
  },

  /** sent → vendor_confirmed — the A1 估計 status flip (S3.2 drawer). */
  acknowledge: async (id: string, note: string): Promise<{ data: PurchaseOrder }> => {
    const { data } = await apiClient.post<{ data: PurchaseOrder }>(
      `${API_ROUTES.PURCHASE_ORDERS}/${id}/acknowledge`,
      { note }
    );
    return data;
  },

  /** The both-documents SO↔PO join read (demand pool + linked refs). */
  listAllocations: async (params?: AllocationListParams): Promise<AllocationListResponse> => {
    const { data } = await apiClient.get<AllocationListResponse>('/allocations', { params });
    return data;
  },

  /** 檢查第二次 aids on demand (open-PO count / lowest recent / duplicate). */
  secondCheck: async (params: {
    productId: string;
    vendorId?: number;
    purchasePrice?: number;
  }): Promise<{ data: PoLineSecondCheck }> => {
    const { data } = await apiClient.get<{ data: PoLineSecondCheck }>(
      `${API_ROUTES.PURCHASE_ORDERS}/second-check`,
      { params }
    );
    return data;
  },
};

export default purchaseOrderApi;
